/**
 * Global Outbound Link Tracking Hook
 * 
 * This hook attaches to the document and tracks ALL outbound link clicks
 * across the entire site - product pages, brand pages, blogs, etc.
 * 
 * Use this at the App level to ensure nothing is missed.
 */

import { useEffect } from 'react';
import { trackEventWithConsent } from '../utils/analyticsWithConsentMode';

export const useGlobalOutboundTracking = () => {
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Find the closest anchor tag (in case click was on child element)
      const link = e.target.closest('a');
      if (!link) return;
      
      const href = link.href;
      if (!href) return;
      
      // Only track external links (not internal navigation)
      try {
        const url = new URL(href);
        const currentDomain = window.location.hostname;
        
        // Skip internal links
        if (url.hostname === currentDomain || url.hostname.includes('originselect')) {
          return;
        }
        
        // Skip javascript: links, mailto:, tel:, etc.
        if (!url.protocol.startsWith('http')) {
          return;
        }
        
        // Determine link type
        const domain = url.hostname.toLowerCase();
        let linkType = 'external';
        let eventName = 'outbound_click';
        let platform = domain;
        const declaredAffiliatePlatform = link.dataset.affiliatePlatform?.trim().toLowerCase();

        // Explicitly declared affiliate links take precedence over domain
        // heuristics. This lets direct merchant links be measured correctly
        // without hard-coding every partner domain in the tracker.
        if (declaredAffiliatePlatform) {
          linkType = 'affiliate';
          eventName = 'affiliate_click';
          platform = declaredAffiliatePlatform;
        }
        // Amazon links
        else if (domain.includes('amazon.')) {
          linkType = 'affiliate';
          eventName = 'affiliate_click';
          platform = domain.includes('amazon.ca') ? 'amazon-ca' : 'amazon-us';
        }
        // Social media
        else if (domain.includes('facebook.') || domain.includes('instagram.') || 
                 domain.includes('pinterest.') || domain.includes('twitter.') ||
                 domain.includes('youtube.') || domain.includes('tiktok.') ||
                 domain.includes('linkedin.')) {
          linkType = 'social';
          eventName = 'social_click';
          platform = domain.split('.')[0].replace('www', '');
        }
        // Government/Authority sites
        else if (domain.includes('.gov') || domain.includes('canada.ca') || 
                 domain.includes('health-canada')) {
          linkType = 'authority';
          eventName = 'outbound_click';
          platform = 'government';
        }
        // Other affiliate networks
        else if (domain.includes('etsy.') || domain.includes('shopify.') ||
                 domain.includes('walmart.') || domain.includes('target.')) {
          linkType = 'affiliate';
          eventName = 'affiliate_click';
          platform = domain.split('.')[0];
        }
        
        // Extract product/link name from context
        const linkText = link.textContent?.trim() || '';
        const productName = extractContextualName(link, linkText);
        
        // Get current page info
        const currentPage = window.location.pathname;
        const pageType = getPageType(currentPage);
        
        // Track the event
        trackEventWithConsent(eventName, {
          event_category: 'Outbound',
          event_label: productName || linkText || href,
          outbound_url: href,
          link_domain: domain,
          link_type: linkType,
          link_text: linkText.substring(0, 100), // Truncate long text
          product_name: productName,
          affiliate_platform: platform,
          page_path: currentPage,
          page_type: pageType,
          is_sponsored: link.rel?.includes('sponsored') || false
        });
        
        console.log(`📊 GLOBAL ${eventName.toUpperCase()}:`, {
          type: linkType,
          platform,
          product: productName,
          url: href.substring(0, 80) + '...',
          page: currentPage
        });
        
      } catch (error) {
        // Invalid URL, skip tracking
        console.warn('Error tracking outbound link:', error);
      }
    };
    
    // Attach to document (captures all clicks via bubbling)
    document.addEventListener('click', handleGlobalClick, true);
    
    console.log('🌐 Global outbound tracking initialized');
    
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, []);
};

/**
 * Extract meaningful name from link context
 */
const extractContextualName = (link, linkText) => {
  // If link text is meaningful, use it
  const genericTexts = [
    'view on amazon', 'shop on amazon', 'buy now', 'shop now', 
    'view', 'buy', 'amazon', 'website', 'visit website', 'click here',
    'learn more', 'read more', 'see more', 'view on amazon.ca'
  ];
  
  if (linkText && !genericTexts.includes(linkText.toLowerCase())) {
    return linkText;
  }
  
  // Try to find product name from parent elements
  const parent = link.closest('.product-card, .product-review, .hub-product-card, article, section, [class*="product"]');
  if (parent) {
    // Look for headings
    const heading = parent.querySelector('h1, h2, h3, h4, .product-name, .product-title, [class*="title"], [class*="name"]');
    if (heading) {
      return heading.textContent?.trim();
    }
  }
  
  // Try data attributes
  if (link.dataset.productName) return link.dataset.productName;
  if (link.dataset.product) return link.dataset.product;
  
  // Try alt text from nearby image
  const nearbyImg = parent?.querySelector('img');
  if (nearbyImg?.alt) {
    return nearbyImg.alt;
  }
  
  return linkText || 'Unknown';
};

/**
 * Determine page type from URL
 */
const getPageType = (pathname) => {
  if (pathname.includes('/blog/')) return 'blog';
  if (pathname.includes('/products/') || pathname.includes('/prod_')) return 'product';
  if (pathname.includes('/brands/')) return 'brand';
  if (pathname.includes('/collections/')) return 'collection';
  if (pathname.includes('/home')) return 'home';
  if (pathname.includes('/global/')) return 'global';
  return 'other';
};

export default useGlobalOutboundTracking;
